import * as React from 'react';
import {
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    IconButton,
    Popover,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector } from 'react-redux';
import { getLocationOptionsData } from '../../../../application/redux/selectors/locationOptions';
import { LabeledData } from '../../../Input';
import { useEffect, useState } from 'react';
import { Spacing } from '../../../Layout';
import LrButton from '../../../LrButton/components/Button';
import { YES_NO_OPTIONS } from '../../../../constants';
import Select from 'components/Select';
import Grid from '@mui/material/Unstable_Grid2';

interface Props {
    onChange?: (value: any) => void;
    currentDeductible?: string;
    tivLabel?: string;
    isPerOccurrenceDefault?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            boxShadow: '0 7px 20px 0 rgba(0,0,0,0.08)',
            borderRadius: '8px',
            padding: theme.spacing(1),
            backgroundColor: theme.palette.background.paper,
            width: '800px',
        },
        checkbox: { padding: '9px', height: '42px', width: '42px' },
    })
);

const WhhDeductibleBuilder: React.FunctionComponent<Props> = (props) => {
    const { onChange, currentDeductible, tivLabel = 'TIV', isPerOccurrenceDefault = false } = props;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const classes = useStyles();

    // Global State
    const locationOptions = useSelector(getLocationOptionsData);

    // Local State

    const parsePercentagePiece = (dedString?: string) => {
        const def = '4%';
        if (!dedString) return def;

        const pattern = /^(\d+[%]).*$/;
        const matchArray = dedString.match(pattern);
        if (matchArray) {
            return matchArray[1];
        }
        return def;
    };
    const parseDollarPiece = (dedString?: string) => {
        const def = '$100,000';
        if (!dedString) return def;

        const pattern = /^([$][\d,]*).*$/;
        const matchArray = dedString.match(pattern);
        if (matchArray) {
            return matchArray[1];
        }
        return def;
    };
    const parseMinimumPiece = (dedString?: string) => {
        if (!dedString) return '';

        const pattern = /^.* Subject to ([$].*) Minimum.*$/i;
        const matchArray = dedString.match(pattern);
        if (matchArray) {
            return matchArray[1];
        }
        return '';
    };

    const [percentagePiece, setPercentagePiece] = useState<string>(parsePercentagePiece(currentDeductible));
    const [minimumPiece, setMinimumPiece] = useState<string>(parseMinimumPiece(currentDeductible));
    const [dollarPiece, setDollarPiece] = useState<string>(parseDollarPiece(currentDeductible));
    const [dedType, setDedType] = useState<string>(currentDeductible?.startsWith('$') ? 'DOLLAR' : 'PERCENT');
    const [isPerBuilding, setIsPerBuilding] = useState<boolean>(currentDeductible?.includes('Building') || false);
    const [isPerOccurrence, setIsPerOccurrence] = useState<boolean>(
        currentDeductible?.includes('Minimum') || isPerOccurrenceDefault
    );

    const buildDeductibleString = () => {
        if (dedType === 'DOLLAR') {
            return `${dollarPiece}${isPerBuilding ? ' Per Building' : ''}${isPerOccurrence ? ' Per Occurrence' : ''}`;
        }
        return `${percentagePiece} of ${tivLabel}${isPerBuilding ? ' Per Building' : ''}${
            minimumPiece ? ', Subject to ' + minimumPiece + ' Minimum' : ''
        }${isPerOccurrence ? ' Per Occurrence' : ''}`;
    };

    const [deductibleString, setDeductibleString] = useState<string>(buildDeductibleString());

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;

    useEffect(() => {
        setDeductibleString(buildDeductibleString());
    }, [dollarPiece, dedType, minimumPiece, isPerOccurrence, isPerBuilding, percentagePiece]);

    useEffect(() => {
        if (currentDeductible) {
            setDeductibleString(currentDeductible);
        }
    }, [currentDeductible]);

    const onCancel = () => {
        setAnchorEl(null);
    };

    const onSubmit = () => {
        onChange && onChange(deductibleString);
        onCancel();
    };

    return (
        <>
            <IconButton size={'small'} style={{ width: '40px' }} onClick={handleClick}>
                <EditIcon style={{ fontSize: 15 }} />
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={onCancel}
            >
                <div className={classes.paper}>
                    <Typography variant={'h2'}>Derived Deductible</Typography>
                    <Typography variant={'h3'}>{deductibleString}</Typography>
                    <Spacing height={'8px'} />
                    <Divider />
                    <Spacing height={'32px'} />

                    <FormControl variant="standard">
                        <FormLabel>Deductible Type</FormLabel>
                        <RadioGroup
                            name="target-type"
                            onChange={(event, value) => setDedType(value)}
                            value={dedType}
                            row
                        >
                            <FormControlLabel
                                value="PERCENT"
                                control={<Radio color="primary" size={'small'} />}
                                label="%"
                            />
                            <FormControlLabel
                                value="DOLLAR"
                                control={<Radio color="primary" size={'small'} />}
                                label="$"
                            />
                        </RadioGroup>
                    </FormControl>
                    <Spacing height={'16px'} />

                    <Grid container justifyContent={'space-between'}>
                        {dedType === 'DOLLAR' ? (
                            <Grid xs={3}>
                                <Select
                                    optionsList={Object.values(locationOptions.LOCATION_WHH_OPTIONS)}
                                    defaultValue={'$100,000'}
                                    value={dollarPiece}
                                    onChange={(event: any) => setDollarPiece(event.target.value)}
                                    selectLabel={'Deductible Amount'}
                                />
                            </Grid>
                        ) : (
                            <>
                                <Grid xs={3}>
                                    <Select
                                        optionsList={Object.values(locationOptions.LOCATION_DED_PERCENTAGES)}
                                        defaultValue={'4%'}
                                        value={percentagePiece}
                                        onChange={(event: any) => setPercentagePiece(event.target.value)}
                                        selectLabel={'Deductible Percentage'}
                                    />
                                </Grid>
                                <Grid xs={3}>
                                    <Select
                                        optionsList={Object.values(locationOptions.LOCATION_DED_MINIMUMS)}
                                        value={minimumPiece}
                                        selectPlaceholder
                                        placeholder={'No Minimum'}
                                        onChange={(event: any) => setMinimumPiece(event.target.value)}
                                        selectLabel={'Deductible Minimum'}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid xs={4}>
                            <FormGroup sx={{ marginTop: '0px' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            className={classes.checkbox}
                                            checked={isPerBuilding}
                                            onChange={(event) => setIsPerBuilding(event.target.checked)}
                                        />
                                    }
                                    label={'Per Building'}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            className={classes.checkbox}
                                            checked={isPerOccurrence}
                                            onChange={(event) => setIsPerOccurrence(event.target.checked)}
                                        />
                                    }
                                    label={'Per Occurrence'}
                                />
                            </FormGroup>
                        </Grid>
                    </Grid>

                    <Spacing height={'24px'} />
                    <LrButton lrType="text" size="large" onClick={onCancel}>
                        {'Cancel'}
                    </LrButton>
                    <LrButton lrType="primary" type="submit" size="large" onClick={onSubmit}>
                        {'OK'}
                    </LrButton>
                </div>
            </Popover>
        </>
    );
};

export default WhhDeductibleBuilder;
